import { Link, useParams } from 'react-router'
import { UserCheck } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError } from '@/api/client'
import { leases as leaseApi, users as userApi } from '@/api/endpoints'
import type { Lease, LeaseState } from '@/api/types'
import { Ago } from '@/components/Ago'
import { Err } from '@/components/Err'
import { Facts } from '@/components/Facts'
import { ToneBadge } from '@/components/ToneBadge'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { leaseChanged, peopleChanged } from '@/hooks/usePage'
import { usePoll } from '@/hooks/usePoll'
import { useRefreshAfterWrites } from '@/hooks/useWorld'
import { diskAllocatedGb, isClosed, isLive, leaseWord, leaseToneOf, requestText, vmGpuText, vmWord, vmSpecText, vmToneOf } from '@/domain/lease'
import { DASH } from '@/ui/format'
import { quotaMemoryText, quotaSizeText, quotaText } from '@/domain/quota'
import { roleLabel, teamColumn, teamRoleWord, PROVIDER_LABEL } from '@/domain/words'
import { personRow, teamsOf } from './model'
import type { World } from './model'
import { VmTags } from './vm/VmTags'

const QUOTA_POLL_MS = 30000
// 사람과 그 사람의 임대는 변경 알림에 다시 읽고(최고 관리자), 알림이 없는 사이는 이 간격이 메운다 — 목록과 같은 간격이다.
const POLL_MS = 8000
// 한 번의 쓰기가 여러 알림을 내므로 모아 한 번 읽는다.
const REFETCH_MS = 1000
// 쿼터가 세는 임대 — 산 넷과 보존 중. VM 절·VM·GPU 수·신청·쿼터의 사용이 이것에서 나온다.
const HELD: LeaseState[] = ['requested', 'pending_approval', 'active', 'ending', 'retired']
// 끝난 임대 — 지난 임대 절. 보존 중은 VM 이 남아도 끝난 임대라 두 읽기에 함께 있다.
const CLOSED: LeaseState[] = ['retired', 'ended', 'rejected', 'failed']
// 지난 임대는 새것부터 이만큼만 보인다.
const PAST_ROWS = 10

// 한 사람의 현재 VM, 신청, 팀, 쿼터 사용량.
// 사람은 `GET /users/{id}` 로 읽는다 — 그 사람에게 닿을 수 없는 관리자(자기 팀 밖의 사람)에게는 서버가 없는 사람과 같은 404 로 답하고
// 화면도 `없는 사용자` 다. 읽기 전에는 없다고 하지 않는다.
// 그 사람의 임대는 서버가 `user` 로 좁혀 준다 — 쿼터가 세는 것(산 넷과 보존 중)을 통째로 읽어 VM·신청·쿼터의 사용을 내고,
// 지난 임대는 새것부터 한 쪽만 따로 읽는다.
// 쿼터의 사용은 서버가 쿼터를 재는 셈과 같다(`usedOf`) — 승인을 기다리는 신청도 세고, 디스크는 임대의 할당이다.

export default function UserView({ world }: { world: World }) {
  const { id = '' } = useParams()
  // 사람에서 사람으로 옮기면(상단 바의 찾기) 경로만 바뀌고 화면은 남는다 — 앞 사람의 쿼터가 새 답이 올 때까지
  // 새 사람의 이름 아래 서지 않게 사람마다 새로 그린다.
  return <UserDetail key={id} id={id} world={world} />
}

function UserDetail({ id, world }: { id: string; world: World }) {
  const person = usePoll(() => (id ? userApi.get(id) : Promise.resolve(null)), useFallback(POLL_MS), [id])
  // 다시 읽은 답이 404 면 그 사람은 이제 닿을 수 없다(관리자가 그 팀에서 빠졌다, 그 사람이 탈퇴했다) — 앞서 읽은 사람을 들고 있으면
  // 다시 읽은 그 사람의 임대가 비어 `VM 없음` 처럼 없는 사실을 그 이름 아래 말한다. 읽은 적이 없는 사람과 같은 `없는 사용자` 다.
  const lost = person.error instanceof ApiError && person.error.status === 404
  const u = lost ? null : person.data
  const found = u !== null
  // 사람을 읽은 뒤에 묻는다 — 닿을 수 없는 사람의 임대·쿼터는 묻지 않는다.
  const held = usePoll(() => (found ? leaseApi.list({ user: id, state: HELD }) : Promise.resolve(null)), useFallback(POLL_MS), [id, found])
  const past = usePoll(
    () => (found ? leaseApi.page({ user: id, state: CLOSED, limit: PAST_ROWS, offset: 0 }) : Promise.resolve(null)),
    useFallback(POLL_MS),
    [id, found],
  )
  const quota = usePoll(() => (found ? userApi.quota(id) : Promise.resolve(null)), QUOTA_POLL_MS, [id, found])
  // 사람과 그 직위는 계정·팀의 알림에, 임대는 임대·VM·이름의 알림에 다시 읽는다. 쓰기 뒤의 다시 읽기에도 모두 단다 —
  // 관리자(제한)는 알림을 받지 않는다.
  useRefetchOn(peopleChanged, person.refresh, REFETCH_MS)
  useRefetchOn(peopleChanged, quota.refresh, REFETCH_MS)
  useRefetchOn(leaseChanged, held.refresh, REFETCH_MS)
  useRefetchOn(leaseChanged, past.refresh, REFETCH_MS)
  useRefreshAfterWrites(person.refresh)
  useRefreshAfterWrites(quota.refresh)
  useRefreshAfterWrites(held.refresh)
  useRefreshAfterWrites(past.refresh)

  if (!u) {
    if (person.error && !(person.error instanceof ApiError && person.error.status === 404)) {
      return (
        <>
          <Crumb steps={[{ label: '사용자', to: '/users' }, { label: '' }]} />
          <Err error={person.error} />
        </>
      )
    }
    return <Empty>{person.loading ? '불러오는 중' : '없는 사용자'}</Empty>
  }

  const p = personRow(u, world.teams)
  const teams = teamsOf(p)
  const leases = held.data
  const live = (leases ?? []).flatMap((l) => (l.vm && isLive(l.vm) ? [l.vm] : []))
  const open = (leases ?? []).filter((l) => l.state !== 'active' && !isClosed(l))
  const closed = past.data?.items ?? []
  const used = leases ? usedOf(leases) : null
  // 아래 읽기들은 사람을 읽은 뒤에 시작한다 — 답이 없고 오류도 없으면 아직 읽는 중이다(`loading` 은 첫 빈 읽기에서 이미 끝났다).
  const reading = (r: { data: unknown; error: Error | null }) => r.data === null && r.error === null
  // 임대를 읽기 전의 수는 모르는 값이다 — 0 이 아니다.
  const count = (n: number, unit: string) => <b className="font-medium text-foreground">{leases ? `${n}${unit}` : DASH}</b>

  return (
    <>
      <Crumb steps={[{ label: '사용자', to: '/users' }, { label: u.username }]} />
      <PageHead
        icon={<UserCheck className="size-5" />}
        kind="사용자"
        title={u.username}
        facts={
          <>
            {roleLabel[u.role]} · VM {count(live.length, '대')} · GPU {count(live.reduce((n, v) => n + v.gpus.length, 0), '장')}
            {teams.length > 0 ? <> · {teamColumn(u.role)} {teams.map((t) => t.display_name).join(', ')}</> : null}
            {u.email ? <> · {u.email}{u.email_verified ? ' (인증됨)' : ' (미인증)'}</> : null}
            {u.has_password === false ? <> · 비밀번호 미설정</> : null}
            {' · 로그인 '}
            {[u.has_password === false ? null : '비밀번호', ...(u.identities ?? []).map((i) => PROVIDER_LABEL[i])].filter(Boolean).join(' · ')}
          </>
        }
      />

      <Err error={held.error} />

      <Section title="VM">
        {leases === null ? (
          reading(held) ? <Empty>불러오는 중</Empty> : null
        ) : live.length === 0 ? (
          <Empty>VM 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>VM</TableHead>
                  <TableHead className="w-24">상태</TableHead>
                  <TableHead className="w-56">사양</TableHead>
                  <TableHead>GPU</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {live.map((v) => (
                  <TableRow key={v.id} className="hover:bg-surface-2">
                    <TableCell className="font-medium max-sm:basis-full">
                      <Link to={`/vms/${v.lease_id}`} className="hover:underline">
                        {v.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ToneBadge tone={vmToneOf(v.state)}>{vmWord(v.state)}</ToneBadge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{vmSpecText(v)}</TableCell>
                    <TableCell className="text-muted-foreground">{vmGpuText(v)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Section title="신청">
        {leases === null ? (
          reading(held) ? <Empty>불러오는 중</Empty> : null
        ) : open.length === 0 ? (
          <Empty>신청 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-24">상태</TableHead>
                  <TableHead>태그</TableHead>
                  <TableHead className="w-64">요청</TableHead>
                  <TableHead className="w-24 text-right">시각</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {open.map((l) => (
                  <TableRow key={l.id} className="hover:bg-transparent">
                    <TableCell>
                      <ToneBadge tone={leaseToneOf(l.state)}>{leaseWord(l.state)}</ToneBadge>
                    </TableCell>
                    <TableCell className="max-sm:basis-full">{l.tags.length > 0 ? <VmTags tags={l.tags} /> : DASH}</TableCell>
                    <TableCell className="text-muted-foreground">{requestText(l)}</TableCell>
                    <TableCell className="text-right text-muted-foreground"><Ago at={l.requested_at} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Section title="쿼터">
        <Err error={quota.error} />
        {quota.data && used ? (
          <div className="rounded-lg border bg-card px-4 py-3">
            <Facts
              rows={[
                // 숫자만 보이면 그 값이 어디서 왔는지 말할 수 없다 — 바꾸러 갈 자리가 직위다.
                ['직위', quota.data.quota_tier?.display_name ?? DASH],
                ['VM', quotaText(used.vms, quota.data.limits.max_vms, '대')],
                ['코어', quotaText(used.cores, quota.data.limits.max_cores)],
                ['메모리', quotaMemoryText(used.mem, quota.data.limits.max_memory_mb)],
                ['디스크', quotaSizeText(used.disk, quota.data.limits.max_disk_gb)],
              ]}
            />
          </div>
        ) : reading(quota) || reading(held) ? (
          <Empty>불러오는 중</Empty>
        ) : null}
      </Section>

      <Section title="팀">
        {p.teams.length === 0 ? (
          <Empty>속한 팀 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            {p.teams.map(({ team, role }) => (
              <div key={team.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                <Link to="/teams" className="flex-1 font-medium hover:underline">
                  {team.display_name}
                </Link>
                <span className="text-[12px] text-muted-foreground">{teamRoleWord(role)}</span>
                {!team.enabled && <ToneBadge tone="warn">비활성</ToneBadge>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="지난 임대">
        <Err error={past.error} />
        {past.data === null ? (
          reading(past) ? <Empty>불러오는 중</Empty> : null
        ) : closed.length === 0 ? (
          <Empty>지난 임대 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-24">결과</TableHead>
                  <TableHead>태그</TableHead>
                  <TableHead>사유</TableHead>
                  <TableHead className="w-24 text-right max-sm:hidden">시작</TableHead>
                  <TableHead className="w-24 text-right max-sm:hidden">끝</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closed.map((l) => (
                  <TableRow key={l.id} className="hover:bg-transparent">
                    <TableCell>
                      <ToneBadge tone={leaseToneOf(l.state)}>{leaseWord(l.state)}</ToneBadge>
                    </TableCell>
                    <TableCell className="max-sm:basis-full">{l.tags.length > 0 ? <VmTags tags={l.tags} /> : DASH}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {l.rejected_reason ?? l.end_reason ?? DASH}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground max-sm:hidden"><Ago at={l.started_at} /></TableCell>
                    <TableCell className="text-right text-muted-foreground max-sm:hidden"><Ago at={l.ended_at} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </>
  )
}

/**
 * 쿼터의 사용 — 서버가 쿼터를 재는 셈과 같다. 받는 것은 쿼터가 세는 임대(산 넷과 보존 중)이고,
 * 보존 중인 임대는 VM·코어·메모리를 놓아 디스크만 센다. 코어·메모리는 신청한 수, 디스크는 루트와 데이터 볼륨을 더한 임대의 할당이다.
 */
function usedOf(leases: Lease[]): { vms: number; cores: number; mem: number; disk: number } {
  const holding = leases.filter((l) => l.state !== 'retired')
  return {
    vms: holding.length,
    cores: holding.reduce((n, l) => n + l.cores, 0),
    mem: holding.reduce((n, l) => n + l.memory_mb, 0),
    disk: leases.reduce((n, l) => n + diskAllocatedGb(l), 0),
  }
}
